import React, { useState } from 'react';
import { PanelBarGLPure, GridSelectionHLPure, ButtonAddHLPure, AlertConfigurationModalPure, InteractiveDialog } from '../../pures';
import { Grid, useMediaQuery } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import './document-panel-bar-alerts.css'
import AlertReminders from '../digital-vault-alerts/alert-reminders';
import { AlertUtils as DigitalVaultAlertUtils } from '../../../utils';
import { ArrowBtnMobile, notificationIcon } from '../../../assets/logos/icons';
import { DateTime } from 'luxon'

import DialogSystemGLPure from '../../pures/dialogSystemGL/dialogSystemGL.pure';

/**
 * @param {{ alerts: import('../../../types/alerts.types').AlertData[], documentData: any, typesDocuments: any[], createAlert: (data: object) => Promise<boolean>, deleteAlert: (alertId: number) => Promise<boolean>, permissions: any }} props
 * @return {*} 
 */
const DocumentPanelBarAlerts = ({ alerts = [], documentData, typesDocuments = [], createAlert, deleteAlert, isInDeleteFlow, permissions }) => {

    const { t } = useTranslation()
    const [fieldsWithError, setFieldsWithError] = useState(/** @type {string[]} */([]))
    const [showConfigurationModal, setShowConfigurationModal] = useState(false)
    const [showDetailsModal, setShowDetailsModal] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    const [initialData, setInitialData] = useState(/** @type {import('../../../types/alerts.types').AlertState} */(DigitalVaultAlertUtils.createDefaultInitialData()))
    const [dataItem, setDataItem] = useState(/** @type {import('../../../types/alerts.types').AlertData | null} */(null))
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false)
    const [alertToDelete, setAlertToDelete] = useState(null)

    const is1040 = useMediaQuery('(max-width:1040px)')
    const is530 = useMediaQuery('(max-width:530px)');


    /**
     * @param {import('@progress/kendo-react-grid').GridHeaderCellProps} props
     */
    const headerColumnOne = (props) => {
        return <span className='documentPanelBarAlerts-header-cell-one'>{props.title}</span>;
    };

    /**
     * @param {import('@progress/kendo-react-grid').GridHeaderCellProps} props
     */
    const headerColumnTwo = (props) => {
        return <span className='documentPanelBarAlerts-header-cell-two'>{props.title}</span>;
    };

    /**
     * @param {import('@progress/kendo-react-grid').GridCellProps} props
     */
    const cellColumnOne = (props) => {
        const alertItem = props.dataItem;

        return (
            <td className={`documentPanelBarAlerts-cell-one`}>
                <div className={'gridHLPure-containerLabel'}>
                    <span className={`gridHLPure-userName`}>
                        {alertItem.name}
                    </span>
                    <div className='gridHLPure-detailsContainer'>
                        <span className='gridHLPure-fieldSubtitle'>
                            {alertItem?.alertTypeRecurrence?.key}
                        </span>
                    </div>
                </div>
            </td>
        );
    };

    /**
     * @param {import('@progress/kendo-react-grid').GridCellProps} props
     */
    const cellColumnTwo = (props) => {
        if (isInDeleteFlow) return null

        return (
            <td className='documentPanelBarAlerts-cell-two'>
                <div
                    className='cellActions-mobileExpanded'
                    onClick={() => onClickAlert(props.dataItem)}
                >
                    <img
                        className='cellActions-icon-arrow'
                        alt={'cellActions-arrow'}
                        src={ArrowBtnMobile}
                    />
                </div>
                <button
                    className='cellActions-btn'
                    onClick={() => onClickAlert(props.dataItem)}
                >
                    <p className='cellActions-textBotton'>
                        {t('documentPanelBarAlerts.alerts.viewDetails')}
                    </p>
                </button>
                <button
                    className='cellActions-TrashBtn'
                    onClick={() => {
                        setDataItem(props.dataItem);
                        setShowDetailsModal(true);
                    }}
                    data-id={'labelTrashActions'}
                >
                    <i title={t('documentPanelBarAlerts.alerts.seeReminders')}>
                        <img width={20} src={notificationIcon} alt={t('documentPanelBarAlerts.alerts.seeReminders')} />
                    </i>
                </button>
                <button
                    className='cellActions-TrashBtn'
                    onClick={() => onDelete(props.dataItem.id)}
                    data-id={'labelTrashActions'}
                >
                    <i
                        title={t('documentPanelBarAlerts.alerts.delete')}
                        className='k-icon k-i-delete k-i-trash'
                    ></i>
                </button>
            </td>
        )
    }

    /**
     * @param {import('../../../types/alerts.types').AlertData} alert
     */
    const onClickAlert = (alert) => {
        setInitialData({ ...DigitalVaultAlertUtils.setDataToForm(alert) })
        setShowConfigurationModal(true)
    }

    const onClose = () => {
        setFieldsWithError([])
        setCurrentStep(0)
        setInitialData(DigitalVaultAlertUtils.createDefaultInitialData())
        if (showDetailsModal) {
            setDataItem(null)
            setShowDetailsModal(false)
        }
        if (showConfigurationModal) setShowConfigurationModal(false)
    }

    const openCreateDialog = () => {
        setShowConfigurationModal(true)
        setInitialData(DigitalVaultAlertUtils.createDefaultInitialData())
    }

    const heightGrid = 'var(--panelHeight)'

    /**
     * Handles changes to daily recurrence settings
     * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
     */
    const handleDailyRecurrenceChange = (e) => {
        if (e.target.value.length > 3) return;
        setInitialData((prev) => DigitalVaultAlertUtils.updateDailyRecurrence(prev, e.target.value));
    }

    /**
     * Handles changes to weekly recurrence settings
     * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
     */
    const handleWeeklyRecurrenceChange = (e) => {
        setInitialData((prev) => DigitalVaultAlertUtils.updateWeeklyRecurrence(prev, e));
    }

    /**
     * Handles changes to monthly recurrence settings
     * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
     * @param {string} type - The type of monthly recurrence ('1' or '2')
     * @param {string} field - The field being changed
     */
    const handleMonthlyRecurrenceChange = (e, type, field) => {
        if (e.target.value.length > 2) return;
        setInitialData(prev => DigitalVaultAlertUtils.updateMonthlyRecurrence(prev, type, field, e.target.value));
    }

    /**
     * Handles changes to yearly recurrence settings
     * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
     * @param {string} type - The type of yearly recurrence ('1' or '2')
     * @param {string} field - The field being changed
     */
    const handleYearlyRecurrenceChange = (e, type, field) => {
        if (field === 'type') {
            setInitialData(prev => ({
                ...prev,
                yearlyRecurrence: {
                    ...prev.yearlyRecurrence,
                    type: e.target.value
                }
            }))
            return;
        }

        if (field === 'times') {
            setInitialData(prev => ({
                ...prev,
                yearlyRecurrence: {
                    ...prev.yearlyRecurrence,
                    times: parseInt(e.target.value)
                }
            }))
            return;
        }

        if (field === 'day' && e.target.value.length > 2) return;

        const value = parseInt(e.target.value);
        setInitialData(prev => ({
            ...prev,
            yearlyRecurrence: {
                ...prev.yearlyRecurrence,
                byDay: type === '1' ? { ...prev.yearlyRecurrence.byDay, [field]: value } : { ...prev.yearlyRecurrence.byDay },
                byOrdinalNumber: type === '2' ? { ...prev.yearlyRecurrence.byOrdinalNumber, [field]: value } : { ...prev.yearlyRecurrence.byOrdinalNumber },
            }
        }))
    }

    /**
     * Handle input changes for form fields
     * @param {any} value - The new value for the field
     * @param {string} field - The field name to update
     */
    const handleInputChange = (value, field) => {
        const normalizedValue = (field === 'startDate' || field === 'endDate') && value instanceof Date
            ? DateTime.fromJSDate(value).startOf('day').toJSDate()
            : value;

        setInitialData(prev => ({
            ...prev,
            [field]: normalizedValue
        }))
    }

    /**
     * Handles adding a document type to the alert
     * @param {import('../../../types/alerts.types').AlertDocumentVariable} docType - Document type object with id, name, and optional comments
     */
    const handleAddDocumentType = (docType) => {
        // Validate that we have at least an id
        if (!docType.documentVariableId) return;

        // Check if document type already exists in the array
        if (initialData.alertDocumentVariables?.find(dt => dt.documentVariableId === docType.documentVariableId)) return;

        // Add the document type to the array
        setInitialData(prev => ({
            ...prev,
            alertDocumentVariables: [...(prev.alertDocumentVariables || []), docType]
        }));

        // Reset the documentType to default state
        setInitialData(prev => ({
            ...prev,
            documentType: {
                id: 0,
                name: 'Selecciona un dato',
                comments: ''
            }
        }));
    }

    /**
     * Handles removing a document type from the alert
     * @param {string|number} docTypeId - ID of the document type to remove
     */
    const handleRemoveDocumentType = (docTypeId) => {
        setInitialData(prev => ({
            ...prev,
            alertDocumentVariables: prev.alertDocumentVariables?.filter(dt => dt.documentVariableId !== docTypeId) || []
        }));
    }

    /**
     * Handles changes to the current document type being edited
     * @param {any} value - The new value
     * @param {string} field - The field being changed ('id', 'name', 'comments')
     */
    const handleDocumentTypeChange = (value, field) => {
        setInitialData(prev => ({
            ...prev,
            documentType: {
                ...prev.documentType,
                [field]: value
            }
        }));
    }

    const onSave = async () => {
        if (!validateData()) return
        const createdAlert = await createAlert(DigitalVaultAlertUtils.prepareAlertDataForBackend(initialData, documentData.id))
        if (!createdAlert) return
        onClose()
    }

    const handleOpenConfirmDelete = (id) => {
        setAlertToDelete(id);
        setShowConfirmDeleteModal(true);
    };

    const handleCloseConfirmDelete = () => {
        setShowConfirmDeleteModal(false);
        setAlertToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!alertToDelete) return;
        const deletedAlert = await deleteAlert(alertToDelete);
        if (!deletedAlert) return;
        handleCloseConfirmDelete();
    };

    const onDelete = (id) => {
        if (!id) return;
        handleOpenConfirmDelete(id);
    };

    const handleNext = () => {
        if (!validateData()) return
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1)
        }
    }

    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1)
        }
    }

    const steps = [
        {
            title: t('documentPanelBarAlerts.dialog.stepper.general-data'),
            icon: process.env.REACT_APP_AWS_S3_BUCKET_URL + 'microservices/MiFirma/assets/alerts/icon_datos_generales_1.svg'
        },
        {
            title: t('documentPanelBarAlerts.dialog.stepper.range-frequency'),
            icon: process.env.REACT_APP_AWS_S3_BUCKET_URL + 'microservices/MiFirma/assets/alerts/icon_rango_frecuencia_2.svg'
        },
        {
            title: t('documentPanelBarAlerts.dialog.stepper.personalized-data'),
            icon: process.env.REACT_APP_AWS_S3_BUCKET_URL + 'microservices/MiFirma/assets/alerts/icon_datos_personalizados_3.svg'
        }
    ]

    const validateData = () => {
        const { isValid, invalidFields } = DigitalVaultAlertUtils.validateData(initialData, currentStep, documentData)
        setFieldsWithError(invalidFields)
        reloadFieldErrors()
        return isValid
    }

    const reloadFieldErrors = () => {
        setTimeout(() => {
            setFieldsWithError([])
        }, 4000);
    }

    const noRecordsMsg = t('digitalVaultUploadView.errors.no.records.alerts')
    return (
        <div className='documentPanelBarAlerts-container'>
            <PanelBarGLPure
                expandedPanel={true}
                titlePanel={t('documentPanelBarAlerts.alerts.title')}
                id='documentPanelBarAlerts'
                actionComponent={<ButtonAddHLPure
                    nameBtn={
                        is1040
                            ? is530
                                ? ''
                                : t('documentPanelBarAlerts.btn.add.alert')
                            : t('documentPanelBarAlerts.btn.add.alert')
                    }
                    onClickBtn={openCreateDialog}
                    iconAdd
                    customIcon=''
                    disabled={isInDeleteFlow || !permissions?.alerts}
                />}
            >
                <Grid item className='documentPanelBarAlerts-grid-documents'>
                    <GridSelectionHLPure
                        styleGeneral={true}
                        styleGeneralClass={'documentPanelBarAlerts-general-grid'}
                        pageable={false}
                        skip={0}
                        take={alerts.length}
                        dataGrid={alerts}
                        noRecordsMessage={noRecordsMsg}
                        filter={true}
                        onSelectedItem={setDataItem}
                        titleRow1={t('documentPanelBarAlerts.header1.title')}
                        titleRow2={t('documentPanelBarAlerts.header2.title')}
                        headerColumnOne={headerColumnOne}
                        headerColumnTwo={headerColumnTwo}
                        columnTwo
                        cellColumnOne={cellColumnOne}
                        cellColumnTwo={cellColumnTwo}
                        // Lock actions column width to avoid shrinking on resize
                        widthColumnTwo={200}
                        minResizableWidth2={200}
                        styleGrid={{ height: heightGrid }}
                        setNameComponentUser={() => { }}
                        setShowDetails={() => { }}
                        iconTrashActive={true}
                    />
                </Grid>
            </PanelBarGLPure>

            {/* Modal para editar alerta - Ver detalles - CON datos de selectedAlert */}
            <AlertConfigurationModalPure
                handleNext={handleNext}
                handlePrevious={handlePrevious}
                steps={steps}
                initialData={initialData}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                validateData={validateData}
                handleInputChange={handleInputChange}
                handleDailyRecurrenceChange={handleDailyRecurrenceChange}
                handleWeeklyRecurrenceChange={handleWeeklyRecurrenceChange}
                handleMonthlyRecurrenceChange={handleMonthlyRecurrenceChange}
                handleYearlyRecurrenceChange={handleYearlyRecurrenceChange}
                handleAddDocumentType={handleAddDocumentType}
                handleRemoveDocumentType={handleRemoveDocumentType}
                handleDocumentTypeChange={handleDocumentTypeChange}
                fieldsWithError={fieldsWithError}
                isOpen={showConfigurationModal}
                onClose={onClose}
                onSave={onSave}
                documentId={documentData?.id}
                typesDocuments={typesDocuments}
            />

            {showDetailsModal && <InteractiveDialog
                title={t('documentPanelBarAlerts.dialog.configuration')}
                onClose={onClose}
            >
                <AlertReminders
                    alert={dataItem}
                    onBack={onClose}
                    t={t}
                />
            </InteractiveDialog>}
            <DialogSystemGLPure
                showConfirm={showConfirmDeleteModal}
                confirmTitle={t('documentPanelBarAlerts.alerts.delete')}
                confirmMessage={t('documentPanelBarAlerts.alerts.delete.message')}
                handleConfirmDeleteFlowClose={handleCloseConfirmDelete}
                handleConfirmDeleteFlowSubmit={handleConfirmDelete}
                labelCancelConfirmation={t('documentPanelBarAlerts.alerts.delete.cancel')}
                labelSubmitConfirmation={t('documentPanelBarAlerts.alerts.delete.confirm')}
            />
        </div>
    );
};

export default DocumentPanelBarAlerts;